import { yupResolver } from '@hookform/resolvers/yup';
import { marketingOptIn } from 'api/marketingOptIn';
import Input from 'components/form/input';
import { useConfig } from 'config';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import emailValidationSchema from 'validations/email';
import * as yup from 'yup';
import Button from '../../../components/button';
import { EventName, trackEvent } from '../../../utils/tracking';

interface FormType {
  newsletter_email: string;
  petType?: 'dog' | 'cat';
}

const NewsletterSection = () => {
  const { commonLinks, frontendUrl } = useConfig();
  const intl = useIntl();
  const { asPath } = useRouter();
  const currentUrl = useMemo(
    () => `${frontendUrl}${asPath.split('#')[0]}`,
    [asPath, frontendUrl]
  );

  const validationSchema = yup.object({
    newsletter_email: emailValidationSchema,
  });

  const { register, handleSubmit, formState } = useForm<FormType>({
    defaultValues: {
      newsletter_email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubscribe = useCallback(
    ({ petType }: { petType: Exclude<FormType['petType'], undefined> }) => {
      return handleSubmit(async (data) => {
        const { newsletter_email } = data;
        trackEvent(EventName.NewsletterSignup);
        await marketingOptIn({
          email: newsletter_email,
          petType: petType,
          source: 'Footer',
          opt_in_url: currentUrl,
        });
      })();
    },
    [currentUrl, handleSubmit]
  );

  return (
    (<div className="flex flex-col items-center justify-center lg:flex-row">
      {formState.isSubmitSuccessful ? (
        <div className="text-center text-base font-black text-other-white md:text-lg">
          <FormattedMessage
            id="footer:newsletter-thank-you"
            values={{ br: <br /> }}
          />
        </div>
      ) : (
        <div className="w-full">
          <Input
            aria-label="E-Mail"
            className="h-10 w-full rounded-sm border bg-other-white px-3 leading-none text-text-light-bg-secondary focus:outline-hidden"
            type="email"
            name="newsletter_email"
            register={register}
            placeholder={intl.formatMessage({
              id: 'footer:newsletter-input-placeholder',
            })}
            formState={formState}
          />

          <div className="-mx-2 flex">
            <div className="w-full px-2">
              <Button
                className="w-full"
                layout="dog"
                disabled={formState.isSubmitting}
                type="submit"
                onClick={() => onSubscribe({ petType: 'dog' })}
                loading={formState.isSubmitting}
              >
                <FormattedMessage id="footer:newsletter-button-dog" />
              </Button>
            </div>
            <div className="w-full px-2">
              <Button
                className="w-full"
                layout="cat"
                disabled={formState.isSubmitting}
                loading={formState.isSubmitting}
                type="submit"
                onClick={() => onSubscribe({ petType: 'cat' })}
              >
                <FormattedMessage id="footer:newsletter-button-cat" />
              </Button>
            </div>
          </div>

          <div className="pt-4 text-xs leading-tight text-text-dark-bg-contrast-white">
            <FormattedMessage
              id="footer:newsletter-unsubscribe-notice"
              values={{
                a: (chunks) => (
                  <Link
                    href={`/pages/${commonLinks.dataProtection}`}
                    passHref
                    className="underline">
                    {chunks}
                  </Link>
                ),
                br: <br />,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </div>
        </div>
      )}
    </div>)
  );
};

export default NewsletterSection;
